/**
 * Erreurs des Cloud Functions de validation (QR de secours, validation manuelle, additions,
 * caisse, migration de programme) → code métier pour le tableau de bord.
 *
 * Les erreurs du serveur portent `details.reason` (session_expired, session_already_used,
 * session_not_found, bad_signature, rate_limited, client_suspended, restaurant_inactive…).
 * Sans réponse exploitable (délai dépassé côté navigateur, réseau coupé pendant l'appel),
 * le crédit a PEUT-ÊTRE été appliqué : on ne dit jamais « QR expiré », on renvoie
 * `no_response` (« vérifiez la fiche du client avant de rescanner »).
 */

const CODE_NAMES = new Set(['internal', 'deadline-exceeded', 'unavailable', 'unknown', '']);

export function callableErrorReason(error: unknown): string {
    const e = error as { code?: unknown; message?: unknown; details?: { reason?: unknown } } | null;
    const reason = e?.details?.reason;
    if (typeof reason === 'string' && reason) return reason;

    const code = typeof e?.code === 'string' ? e.code : '';
    const message = typeof e?.message === 'string' ? e.message.trim() : '';
    if (code === 'functions/resource-exhausted') return 'rate_limited';
    if (code === 'functions/unavailable' || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
        return 'offline';
    }
    // Message générique = le nom du code : la réponse du serveur n'est pas arrivée.
    if ((code === 'functions/internal' || code === 'functions/deadline-exceeded') && CODE_NAMES.has(message)) {
        return 'no_response';
    }
    if (code.startsWith('functions/') && message && !CODE_NAMES.has(message)) return message;
    return 'no_response';
}

/** Libellé français d'un code métier (ou le message serveur, déjà en français). */
export function checkinMessage(reason: string): string {
    switch (reason) {
        case 'no_response':
            return 'Réponse non reçue : vérifiez la fiche du client avant de rescanner.';
        case 'offline':
            return 'Pas de connexion internet : vérifiez le Wi-Fi ou les données mobiles.';
        case 'rate_limited':
            return 'Trop de validations en peu de temps. Patientez quelques minutes.';
        case 'session_not_found':
            return 'QR introuvable : demandez au client d\'afficher un nouveau QR dans l\'application.';
        case 'bad_signature':
            return 'QR non reconnu (signature invalide).';
        case 'client_suspended':
            return 'Le compte de ce client est suspendu.';
        case 'user_suspended':
            return 'Votre compte est suspendu.';
        case 'restaurant_inactive':
            return 'Votre restaurant n\'est pas actif : validation impossible.';
        case 'not_member':
            return 'Ce client n\'est pas inscrit à votre programme.';
        case 'no_credit_without_amount':
            return 'Votre programme crédite sur le montant de l\'addition : préparez-la dans « Encaissement fidélité », puis scannez le QR depuis l\'écran d\'attente.';
        case 'target_gone':
            return 'Cette addition n\'est plus disponible (déjà validée, annulée ou expirée) ou n\'est pas liée à ce client.';
        case 'already_validated_today':
            return 'Repas déjà validé aujourd\'hui pour ce client.';
        case 'already_paid':
            return 'Cette commande est déjà encaissée.';
        case 'not_payable':
            return 'Cette commande n\'est pas prête à être encaissée : rouvrez-la puis cliquez sur « Encaisser ».';
        case 'amount_too_high':
            return 'Montant de 1 000 € ou plus : la commande est payée, mais la fidélité n\'est pas créditée.';
        case 'no_program':
            return 'Aucun programme de fidélité enregistré.';
        default:
            return reason || 'Erreur de validation.';
    }
}
